import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import Client from "fhir-kit-client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Title } from "@fyrstain/hl7-front-library";
import JanusPage from "../../components/JanusPage/JanusPage";
import LoadingButton from "../../components/LoadingButton/LoadingButton";
import CodeEditor from "../../components/CodeEditor/CodeEditor";
import StructureMapFlowEditor from "../../components/StructureMapFlowEditor/StructureMapFlowEditor";
import { getStructureMapValidationErrors } from "../../components/StructureMapVisualEditor/StructureMapVisualEditor";
import styles from "./EditStructureMap.module.css";

type JsonRecord = Record<string, any>;
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function newStructureMap(): JsonRecord {
  return {
    resourceType: "StructureMap",
    url: "http://example.org/fhir/StructureMap/new-map",
    name: "NewStructureMap",
    title: "Nouvelle StructureMap",
    status: "draft",
    structure: [
      { url: "http://hl7.org/fhir/StructureDefinition/Patient", mode: "source", alias: "source" },
      { url: "http://hl7.org/fhir/StructureDefinition/Patient", mode: "target", alias: "target" },
    ],
    group: [
      {
        name: "main",
        typeMode: "none",
        input: [
          { name: "source", type: "Patient", mode: "source" },
          { name: "target", type: "Patient", mode: "target" },
        ],
        rule: [
          {
            name: "copyId",
            source: [{ context: "source", element: "id", variable: "id" }],
            target: [
              {
                context: "target",
                contextType: "variable",
                element: "id",
                transform: "copy",
                parameter: [{ valueId: "id" }],
              },
            ],
          },
        ],
      },
    ],
  };
}

const EditStructureMap: FunctionComponent = () => {
  const { structureMapId } = useParams();
  const isNew = structureMapId === "new";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fhirClient = useMemo(() => new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" }), []);
  const [structureMap, setStructureMap] = useState<JsonRecord>();
  const [jsonContent, setJsonContent] = useState("");
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const replaceMap = useCallback((next: JsonRecord) => {
    setStructureMap(next);
    setJsonContent(JSON.stringify(next, null, "\t"));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    if (isNew) {
      replaceMap({ ...newStructureMap(), title: t("structureMap.newTitle") });
      setLoading(false);
      return;
    }
    try {
      const response = await fhirClient.read({ resourceType: "StructureMap", id: structureMapId ?? "" });
      if (response.resourceType !== "StructureMap") throw new Error("La ressource chargée n’est pas une StructureMap.");
      replaceMap(response as JsonRecord);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Impossible de charger la StructureMap.");
    } finally {
      setLoading(false);
    }
  }, [fhirClient, isNew, replaceMap, structureMapId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyJsonToGraphicEditor = () => {
    try {
      const parsed = JSON.parse(jsonContent) as JsonRecord;
      if (!parsed || Array.isArray(parsed)) throw new Error("La racine doit être un objet JSON.");
      replaceMap(parsed);
      setError(undefined);
      setIsJsonMode(false);
    } catch (parseError) {
      setError(parseError instanceof Error ? `JSON invalide : ${parseError.message}` : "JSON invalide.");
    }
  };

  const updateMapField = (field: string, value: string) => {
    if (!structureMap) return;
    const next = clone(structureMap);
    next[field] = value;
    replaceMap(next);
  };

  const save = async () => {
    let mapToSave = structureMap;
    if (isJsonMode) {
      try {
        mapToSave = JSON.parse(jsonContent) as JsonRecord;
      } catch (parseError) {
        setError(parseError instanceof Error ? `JSON invalide : ${parseError.message}` : "JSON invalide.");
        return;
      }
    }
    if (!mapToSave) return;
    const validation = getStructureMapValidationErrors(mapToSave);
    if (validation.length) {
      setError(validation.join(" "));
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      if (isNew) {
        const created = (await fhirClient.create({
          resourceType: "StructureMap",
          body: mapToSave as any,
        })) as JsonRecord;
        if (!created.id) throw new Error("Le serveur n’a pas retourné l’identifiant de la StructureMap créée.");
        navigate(`/EditStructureMap/${created.id}`, { replace: true });
      } else {
        await fhirClient.update({ resourceType: "StructureMap", id: structureMapId ?? "", body: mapToSave as any });
        replaceMap(mapToSave);
      }
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Impossible d’enregistrer la StructureMap.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <JanusPage
      titleKey={isNew ? "structureMap.newTitle" : "structureMap.editorTitle"}
      loading={loading}
      needsLogin={true}
    >
      <div className={styles.page}>
        <div className={styles.header}>
          <div>
            <Title level={2} content={isNew ? t("structureMap.createTitle") : t("structureMap.editorTitle")} />
            <p>{t("structureMap.intro")}</p>
          </div>
          <div className={styles.actions}>
            {!isNew && (
              <Button variant="outline-secondary" onClick={() => navigate(`/StructureMap/${structureMapId}`)}>
                {t("structureMap.backToTests")}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={() => (isJsonMode ? applyJsonToGraphicEditor() : setIsJsonMode(true))}
            >
              {isJsonMode ? t("structureMap.applyJson") : t("structureMap.editJson")}
            </Button>
            <LoadingButton
              icon={faSave}
              isLoading={saving}
              onClick={save}
              enabledTooltipText={isNew ? t("structureMap.createSave") : t("structureMap.save")}
            />
          </div>
        </div>
        {error && <Alert variant="danger">{error}</Alert>}
        {structureMap && (
          <>
            <Card className={[styles.card, styles.metadata].join(" ")}>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("structureMap.field.technicalName")}</Form.Label>
                      <Form.Control
                        value={structureMap.name ?? ""}
                        onChange={(event) => updateMapField("name", event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("structureMap.field.title")}</Form.Label>
                      <Form.Control
                        value={structureMap.title ?? ""}
                        onChange={(event) => updateMapField("title", event.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("structureMap.field.status")}</Form.Label>
                      <Form.Select
                        value={structureMap.status ?? "draft"}
                        onChange={(event) => updateMapField("status", event.target.value)}
                      >
                        <option value="draft">draft</option>
                        <option value="active">active</option>
                        <option value="retired">retired</option>
                        <option value="unknown">unknown</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mt-2">
                  <Form.Label>{t("structureMap.field.canonicalUrl")}</Form.Label>
                  <Form.Control
                    value={structureMap.url ?? ""}
                    onChange={(event) => updateMapField("url", event.target.value)}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
            {isJsonMode ? (
              <Card className={styles.card}>
                <Card.Body>
                  <CodeEditor content={jsonContent} setContent={setJsonContent} />
                </Card.Body>
              </Card>
            ) : (
              <Card className={styles.card}>
                <Card.Body>
                  <StructureMapFlowEditor structureMap={structureMap} onChange={replaceMap} />
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </JanusPage>
  );
};

export default EditStructureMap;
